/**
 * Retry policy shared by single Graph requests and $batch sub-requests.
 * Honors Retry-After on 429/503, otherwise exponential backoff with jitter.
 */
export const MAX_RETRIES = 5;
export const BASE_DELAY_MS = 1000;
export const MAX_DELAY_MS = 30_000;
/**
 * A Retry-After from Graph/SharePoint is honoured in full (G10: cutting it
 * short just earns another throttle) — but one call never waits more than
 * RETRY_BUDGET_MS in total: past that it fails ("busy, try again"), so a
 * Save button or page load doesn't spin for minutes. The background sync
 * engine has its own backoff on top and simply tries again later.
 */
export const MAX_RETRY_AFTER_MS = 120_000;
export const RETRY_BUDGET_MS = 90_000;

/**
 * The wait before retry `attempt`, or null = give up now (out of attempts,
 * or the wait would take this call past its budget).
 */
export function nextRetryDelay(attempt: number, retryAfterSeconds: number | undefined, waitedMs: number): number | null {
  if (attempt >= MAX_RETRIES) return null;
  const delay = retryDelayMs(attempt, retryAfterSeconds);
  return waitedMs + delay > RETRY_BUDGET_MS ? null : delay;
}

export function isRetryableStatus(status: number): boolean {
  return status === 429 || status === 502 || status === 503 || status === 504;
}

/**
 * Delay before retry `attempt` (0-based). `retryAfterSeconds` comes from the
 * Retry-After header when the service provided one and always wins.
 */
export function retryDelayMs(
  attempt: number,
  retryAfterSeconds?: number,
): number {
  if (retryAfterSeconds !== undefined && retryAfterSeconds >= 0) {
    return Math.min(retryAfterSeconds * 1000, MAX_RETRY_AFTER_MS);
  }
  const exp = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
  // Full jitter keeps concurrent clients from retrying in lockstep.
  return Math.floor(exp / 2 + Math.random() * (exp / 2));
}

export function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return seconds;
  const date = Date.parse(header);
  if (!Number.isNaN(date)) {
    return Math.max(0, Math.ceil((date - Date.now()) / 1000));
  }
  return undefined;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
